import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StudentForm from '../components/StudentForm';
import EmptyState from '../components/EmptyState';
import NationalityDisplay from '../components/NationalityDisplay';
import NationalitySelect from '../components/NationalitySelect';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import { useAuth } from '../contexts/AuthContext';
import { atPlanLimit, planFeatures } from '../utils/planFeatures';
import './Students.css';

const getDocumentTypeLabel = (docType) => {
  if (!docType) return docType;
  if (docType === 'NIE') return 'Carnet de extranjería';
  return docType;
};

const getParentRelationshipLabel = (relationship) => {
  const value = (relationship || '').trim();
  return value || 'Padre/Tutor';
};

const Students = () => {
  const { user } = useAuth();
  const STUDENTS_PAGE_SIZE = 25;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showForm, setShowForm] = useState(false); // Estado para mostrar formulario inline
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Estado local para el input
  const [page, setPage] = useState(1);
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [nationality, setNationality] = useState('');
  const [disciplineId, setDisciplineId] = useState('');
  const searchInputRef = useRef(null); // Ref para mantener el foco
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [emailModal, setEmailModal] = useState({
    isOpen: false,
    student: null
  });
  const [bulkUploadModal, setBulkUploadModal] = useState({
    isOpen: false,
    uploading: false,
    result: null
  });
  const fileInputRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const showConfirm = (title, message, onConfirm, type = 'warning') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const closeConfirm = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      type: 'warning'
    });
  };

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 300); // Esperar 300ms después de que el usuario deje de escribir

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Manejar el cambio de búsqueda sin perder el foco
  const handleSearchChange = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const value = e.target.value;
    setSearchInput(value);
  }, []);

  const { data: studentsData, isLoading, isFetching, error } = useQuery(
    ['students', search, page, STUDENTS_PAGE_SIZE, ageMin, ageMax, /^\d{4}$/.test(String(birthYear)) ? birthYear : '', nationality, disciplineId],
    async () => {
      const params = {
        is_active: true,
        skip: (page - 1) * STUDENTS_PAGE_SIZE,
        limit: STUDENTS_PAGE_SIZE,
      };
      if (search) params.search = search.trim();
      if (ageMin !== '') params.age_min = ageMin;
      if (ageMax !== '') params.age_max = ageMax;
      const validBirthYear = /^\d{4}$/.test(String(birthYear))
        && Number(birthYear) >= 1900
        && Number(birthYear) <= 2200
        ? String(birthYear)
        : '';
      if (validBirthYear) params.birth_year = validBirthYear;
      if (nationality) params.nationality_code = nationality;
      if (disciplineId) params.discipline_id = disciplineId;
      const response = await api.get('/students', { params });
      const totalHeader = response.headers['x-total-count'];
      const total = totalHeader ? parseInt(totalHeader, 10) : response.data.length;
      return {
        items: response.data,
        total: Number.isNaN(total) ? response.data.length : total,
      };
    },
    {
      retry: 1,
      keepPreviousData: true,
      staleTime: 5000,
      onError: (err) => {
        if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
          toast.error('No se pudo conectar con el servidor. Verifica que el backend esté corriendo.');
        } else if (err.response?.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        } else {
          toast.error(err.response?.data?.detail || 'Error al cargar alumnos');
        }
      },
    }
  );

  const { data: studentDisciplines = [] } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data || [];
  });

  const students = studentsData?.items || [];
  const totalStudents = studentsData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalStudents / STUDENTS_PAGE_SIZE));
  const pageStart = totalStudents === 0 ? 0 : (page - 1) * STUDENTS_PAGE_SIZE + 1;
  const pageEnd = Math.min(page * STUDENTS_PAGE_SIZE, totalStudents);

  const createStudentMutation = useMutation(
    (data) => api.post('/students', data),
    {
      onSuccess: () => {
        setPendingStudents((current) => current + 1);
        queryClient.invalidateQueries('students');
        toast.success('Alumno creado exitosamente!');
        setIsModalOpen(false);
        setShowForm(false);
        setEditingStudent(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          // Errores de validación de Pydantic
          const errorMessages = errorDetail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          toast.error(`Error de validación: ${errorMessages}`);
        } else {
          toast.error(errorDetail || 'Error al crear alumno');
        }
      },
    }
  );

  const updateStudentMutation = useMutation(
    ({ id, data }) => api.put(`/students/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students');
        toast.success('Alumno actualizado exitosamente!');
        setIsModalOpen(false);
        setShowForm(false);
        setEditingStudent(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al actualizar alumno');
      },
    }
  );

  const toggleStudentStatusMutation = useMutation(
    ({ id, is_active }) => api.put(`/students/${id}`, { is_active }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('students');
        toast.success('Estado del alumno actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al cambiar estado');
      },
    }
  );

  const deleteStudentMutation = useMutation(
    (studentId) => api.delete(`/students/${studentId}`),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('students');
        const studentName = response.data?.student_name || 'el alumno';
        toast.success(`Alumno "${studentName}" eliminado exitosamente`);
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al eliminar alumno');
      },
    }
  );

  const maxStudents = planFeatures(user).max_students;
  const [pendingStudents, setPendingStudents] = useState(0);
  const { data: activeStudentCount = 0 } = useQuery(
    ['students', 'plan-count', maxStudents],
    async () => {
      const response = await api.get('/students', { params: { is_active: true, limit: 1 } });
      const totalHeader = response.headers['x-total-count'];
      const total = totalHeader ? parseInt(totalHeader, 10) : response.data.length;
      return Number.isNaN(total) ? response.data.length : total;
    },
    { enabled: maxStudents != null, keepPreviousData: true }
  );
  const listCountsAllActive = !search && ageMin === '' && ageMax === '' && !birthYear && !nationality && !disciplineId;
  const knownActiveStudents = (listCountsAllActive && studentsData ? studentsData.total : activeStudentCount) + pendingStudents;
  const studentLimitReached = atPlanLimit(knownActiveStudents, maxStudents);

  useEffect(() => {
    setPendingStudents(0);
  }, [studentsData?.total, activeStudentCount]);

  const handleCreateClick = () => {
    if (studentLimitReached) {
      toast.error(`El plan Free permite hasta ${maxStudents} alumnos.`);
      return;
    }
    setEditingStudent(null);
    setShowForm(true);
  };

  // Abrir modal automáticamente si viene con action=create
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      handleCreateClick();
      // Limpiar el parámetro de la URL
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEditClick = (student) => {
    // Formatear fecha para el input date
    const formattedStudent = {
      ...student,
      birth_date: student.birth_date ? student.birth_date.split('T')[0] : '',
    };
    setEditingStudent(formattedStudent);
    setShowForm(true);
  };

  const handleUpdateEmailClick = (student) => {
    setEmailModal({ isOpen: true, student });
  };

  const handleUpdateEmailSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const emailData = {
      resend_notifications: formData.get('resend_notifications') === 'on',
    };

    const parentEmailInput = (formData.get('parent_email') || '').trim();
    const studentEmail = (formData.get('student_email') || '').trim();
    const age = calculateAge(emailModal.student.birth_date);
    const parentWasEmpty = !(emailModal.student.parent_email || '').trim();
    const parentEmail = (
      !parentEmailInput && studentEmail && parentWasEmpty && typeof age === 'number' && age < 18
    )
      ? studentEmail
      : parentEmailInput;
    const studentPhone = (formData.get('student_phone') || '').trim();
    const parentPhone = (formData.get('parent_phone') || '').trim();

    if (parentEmail) emailData.parent_email = parentEmail;
    if (studentEmail) emailData.student_email = studentEmail;
    if (studentPhone) emailData.student_phone = cleanPhoneNumber(studentPhone);
    if (parentPhone) emailData.parent_phone = cleanPhoneNumber(parentPhone);
    
    updateEmailMutation.mutate({
      studentId: emailModal.student.id,
      emailData
    });
  };

  const handleSubmit = (data) => {
    // Limpiar campos opcionales: convertir strings vacíos, null, undefined o "string" a null
    const cleanedData = {};
    
    // Campos obligatorios (siempre se incluyen)
    cleanedData.first_name = data.first_name?.trim() || '';
    cleanedData.last_name = data.last_name?.trim() || '';
    cleanedData.birth_date = data.birth_date || '';
    
    // Función helper para limpiar valores opcionales
    const cleanOptionalField = (value) => {
      if (value === null || value === undefined || value === '' || value === 'string') {
        return null;
      }
      const trimmed = typeof value === 'string' ? value.trim() : value;
      return trimmed === '' ? null : trimmed;
    };
    
    // Campos opcionales: convertir strings vacíos, null, undefined o "string" a null
    cleanedData.gender = cleanOptionalField(data.gender);
    cleanedData.document_type = cleanOptionalField(data.document_type);
    cleanedData.document_number = cleanOptionalField(data.document_number);
    cleanedData.email = cleanOptionalField(data.email);
    // Limpiar números de teléfono antes de guardar
    const phoneValue = cleanOptionalField(data.phone);
    cleanedData.phone = phoneValue ? cleanPhoneNumber(phoneValue) : null;
    cleanedData.parent_name = cleanOptionalField(data.parent_name);
    const parentPhoneValue = cleanOptionalField(data.parent_phone);
    cleanedData.parent_phone = parentPhoneValue ? cleanPhoneNumber(parentPhoneValue) : null;
    cleanedData.parent_email = cleanOptionalField(data.parent_email);
    cleanedData.parent_relationship = cleanOptionalField(data.parent_relationship);
    cleanedData.address = cleanOptionalField(data.address);
    cleanedData.country = cleanOptionalField(data.country);
    cleanedData.nationality_code = cleanOptionalField(data.nationality_code);
    cleanedData.city = cleanOptionalField(data.city);
    cleanedData.postal_code = cleanOptionalField(data.postal_code);
    cleanedData.medical_notes = cleanOptionalField(data.medical_notes);
    cleanedData.uniform_size = cleanOptionalField(data.uniform_size);
    
    if (editingStudent) {
      updateStudentMutation.mutate({ id: editingStudent.id, data: cleanedData });
    } else {
      createStudentMutation.mutate(cleanedData);
    }
  };

  const selectedStudents = (students || []).filter((student) => selectedIds.includes(student.id));

  const toggleStudentSelection = (studentId) => {
    setSelectedIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = (students || []).map((student) => student.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  const runOnSelected = (action, { singleOnly = false } = {}) => {
    if (!selectedStudents.length) {
      toast.info('Selecciona al menos un alumno');
      return;
    }
    if (singleOnly && selectedStudents.length !== 1) {
      toast.info('Selecciona un solo alumno para esta acción');
      return;
    }
    if (singleOnly) {
      action(selectedStudents[0]);
      return;
    }
    selectedStudents.forEach(action);
  };

  const handleToggleStatus = (student) => {
    const deactivating = student.is_active;
    showConfirm(
      `¿${deactivating ? 'Desactivar' : 'Activar'} a ${student.full_name}?`,
      deactivating
        ? 'Dejará de aparecer en la lista de alumnos. Su información se conserva y se puede restaurar con su DNI.'
        : 'Volverá a aparecer en la lista de alumnos.',
      () => toggleStudentStatusMutation.mutate({
        id: student.id,
        is_active: !student.is_active
      }),
      deactivating ? 'warning' : 'success'
    );
  };

  const handleDeleteStudent = (student) => {
    setConfirmDialog({
      isOpen: true,
      title: `¿Estás seguro de que deseas ELIMINAR a ${student.full_name}?`,
      message: 'El alumno dejará de aparecer en la lista, pero su información se conservará y podrá restaurarse en el futuro utilizando su DNI.',
      onConfirm: () => deleteStudentMutation.mutate(student.id),
      type: 'danger',
      confirmText: 'Aceptar',
      cancelText: 'Cancelar'
    });
  };

  const handleGenerateCertificate = async (student) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      const url = `${baseUrl}/reports/attendance-certificate/${student.id}/pdf`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al generar el certificado');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `certificado_asistencia_${student.first_name}_${student.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Certificado generado exitosamente');
    } catch (error) {
      toast.error('Error al generar el certificado. Por favor, intente nuevamente.');
    }
  };

  const handleDownloadQR = async (student) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      // Evita PNG en caché del navegador de versiones anteriores (solo QR, sin nombre/DNI)
      const url = `${baseUrl}/students/${student.id}/qr-image?cb=${Date.now()}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al generar el código QR');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Nombre personalizado: QR_NombreEstudiante.png
      const safeName = `${student.first_name}_${student.last_name}`.replace(/[^a-zA-Z0-9_]/g, '_');
      link.download = `QR_${safeName}.png`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Código QR descargado exitosamente');
    } catch (error) {
      toast.error('Error al descargar el código QR. Por favor, intente nuevamente.');
    }
  };

  const updateEmailMutation = useMutation(
    ({ studentId, emailData }) => api.put(`/students/${studentId}/update-email`, emailData),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('students');
        const data = response.data;
        if (data?.notification_sent) {
          toast.success(`Contacto actualizado y notificaciones reenviadas a ${data.notification_recipient}`);
        } else if (data?.resend_notifications_requested) {
          const copied = data.parent_email_copied
            ? ' El correo también quedó registrado como el del padre/tutor.'
            : '';
          toast.warning(`${data.message || data.error || 'Contacto actualizado, pero no se pudo reenviar el correo'}${copied}`);
        } else {
          toast.success(data?.message || 'Contacto actualizado exitosamente');
        }
        setEmailModal({ isOpen: false, student: null });
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al actualizar emails');
      },
    }
  );

  const sendQREmailMutation = useMutation(
    ({ studentId, sendToParent }) => {
      const params = sendToParent ? { send_to_parent: true } : {};
      return api.post(`/students/${studentId}/send-qr-email`, {}, { params });
    },
    {
      onSuccess: (response) => {
        toast.success(response.data?.message || 'Código QR enviado por email exitosamente');
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail || 'Error al enviar el email';
        toast.error(errorDetail);
      },
    }
  );

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      const url = `${baseUrl}/students-bulk/template`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar la plantilla');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'plantilla_estudiantes.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Plantilla descargada exitosamente');
    } catch (error) {
      toast.error('Error al descargar la plantilla. Por favor, intente nuevamente.');
    }
  };

  const handleBulkUploadClick = () => {
    setBulkUploadModal({ isOpen: true, uploading: false, result: null });
  };

  const handleCloseBulkUploadModal = () => {
    // Si hay resultados, mostrar confirmación antes de cerrar
    if (bulkUploadModal.result) {
      setConfirmDialog({
        isOpen: true,
        title: '¿Cerrar resultados de carga masiva?',
        message: 'Se cerrarán los resultados de la carga. Asegúrate de haber revisado los errores si los hubo.',
        onConfirm: () => {
          setBulkUploadModal({ isOpen: false, uploading: false, result: null });
          // Limpiar input de archivo
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
        type: 'warning',
        confirmText: 'Cerrar',
        cancelText: 'Revisar'
      });
    } else {
      // Si no hay resultados, cerrar directamente
      setBulkUploadModal({ isOpen: false, uploading: false, result: null });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar que sea un archivo Excel
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)');
      return;
    }

    // Mostrar confirmación antes de procesar
    setConfirmDialog({
      isOpen: true,
      title: '¿Iniciar carga masiva?',
      message: `Estás a punto de procesar el archivo "${file.name}". Los estudiantes válidos serán creados automáticamente en el sistema.`,
      onConfirm: () => processExcelFile(file),
      type: 'info',
      confirmText: 'Procesar',
      cancelText: 'Cancelar'
    });
  };

  const processExcelFile = async (file) => {
    setBulkUploadModal(prev => ({ ...prev, uploading: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/students-bulk/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setBulkUploadModal({
        isOpen: true,
        uploading: false,
        result: response.data
      });

      // Refrescar lista de estudiantes
      queryClient.invalidateQueries('students');

      if (response.data.created > 0) {
        toast.success(`${response.data.created} estudiantes creados exitosamente`);
      }
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} filas con errores`);
      }
    } catch (error) {
      setBulkUploadModal(prev => ({ ...prev, uploading: false }));
      toast.error(error.response?.data?.detail || 'Error al procesar el archivo');
    }

    // Limpiar input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendQREmail = async (student, sendToParent = false) => {
    if (!student.qr_code) {
      toast.error('El estudiante no tiene un código QR asignado');
      return;
    }

    // Verificar que haya un email disponible
    if (sendToParent) {
      if (!student.parent_email) {
        if (student.email) {
          showConfirm(
            '⚠️ Email de padre/tutor no disponible',
            `El estudiante ${student.full_name} no tiene email de padre/tutor registrado.\n\n¿Deseas enviar el código QR al email del estudiante (${student.email}) en su lugar?`,
            () => sendQREmailMutation.mutate({ studentId: student.id, sendToParent: false }),
            'warning'
          );
        } else {
          toast.error('El estudiante no tiene email ni email de padre/tutor registrado. Por favor, actualiza los datos del estudiante.');
        }
        return;
      }
    } else {
      if (!student.email) {
        if (student.parent_email) {
          showConfirm(
            '⚠️ Email del estudiante no disponible',
            `El estudiante ${student.full_name} no tiene email registrado.\n\n¿Deseas enviar el código QR al email del padre/tutor (${student.parent_email}) en su lugar?`,
            () => sendQREmailMutation.mutate({ studentId: student.id, sendToParent: true }),
            'warning'
          );
        } else {
          toast.error('El estudiante no tiene email registrado. Por favor, actualiza los datos del estudiante.');
        }
        return;
      }
    }

    const recipient = sendToParent 
      ? student.parent_email
      : student.email;
    
    const recipientType = sendToParent
      ? getParentRelationshipLabel(student.parent_relationship).toLowerCase()
      : 'estudiante';
    
    // Confirmación mejorada con más detalles
    showConfirm(
      '📧 Confirmar envío de código QR',
      `Estudiante: ${student.full_name}\nEmail destino: ${recipient} (${recipientType})\n\n¿Estás seguro de enviar el código QR por email?`,
      () => {
        sendQREmailMutation.mutate({ 
          studentId: student.id, 
          sendToParent 
        });
      },
      'info'
    );
  };

  const sendQrToSelected = (sendToParent) => {
    const ready = [];
    const skipped = [];
    selectedStudents.forEach((student) => {
      if (!student.qr_code) {
        skipped.push(`${student.full_name}: sin QR`);
        return;
      }
      const email = sendToParent ? student.parent_email : student.email;
      if (!email) {
        skipped.push(`${student.full_name}: sin correo de ${sendToParent ? 'padre/tutor' : 'estudiante'}`);
        return;
      }
      ready.push(student);
    });
    if (!ready.length) {
      toast.error('Ningún alumno seleccionado tiene correo para este envío');
      return;
    }
    const who = sendToParent ? 'padre, madre o tutor' : 'estudiante';
    const preview = ready.length <= 5
      ? `\n\n${ready.map((student) => {
          const email = sendToParent ? student.parent_email : student.email;
          const label = sendToParent
            ? getParentRelationshipLabel(student.parent_relationship)
            : 'Estudiante';
          return `${student.full_name} → ${email} (${label})`;
        }).join('\n')}`
      : '';
    const skipNote = skipped.length
      ? `\n\n${skipped.length} alumno${skipped.length === 1 ? '' : 's'} no ${skipped.length === 1 ? 'tiene' : 'tienen'} correo de ${who} y no se ${skipped.length === 1 ? 'incluye' : 'incluyen'}.`
      : '';
    showConfirm(
      `Enviar ${ready.length} código${ready.length === 1 ? '' : 's'} QR`,
      `Se enviará un correo por alumno al ${who}.${preview}${skipNote}`,
      async () => {
        let sent = 0;
        const failed = [];
        for (const student of ready) {
          try {
            await api.post(`/students/${student.id}/send-qr-email`, {}, {
              params: sendToParent ? { send_to_parent: true } : {},
            });
            sent += 1;
          } catch (err) {
            failed.push(student.full_name);
          }
        }
        if (sent) toast.success(`${sent} correo${sent === 1 ? '' : 's'} enviado${sent === 1 ? '' : 's'}`);
        if (failed.length) toast.error(`No se pudo enviar a: ${failed.join(', ')}`);
      },
      'info'
    );
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (isLoading && !studentsData) {
    return <div className="loading">Cargando alumnos...</div>;
  }

  if (error) {
    const errorMessage = error.response?.data?.detail || 
                        (error.code === 'ECONNABORTED' || error.message === 'Network Error' 
                          ? 'No se pudo conectar con el servidor. Verifica que el backend esté corriendo en http://localhost:8000'
                          : error.message || 'Error desconocido');
    
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar alumnos</h3>
          <p>{errorMessage}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('students')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    <div className="page-container">
      <div className="page-header students-page-header">
        <div className="students-page-header-top">
          <h2>Gestión de Alumnos</h2>
          {!showForm && (
            <div className="students-page-actions">
              <button className="btn btn-success" onClick={handleDownloadTemplate} title="Descargar plantilla Excel">
                📥 Descargar Plantilla
              </button>
              <button className="btn btn-info" onClick={handleBulkUploadClick} title="Carga masiva desde Excel">
                📤 Carga Masiva
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateClick}
                disabled={studentLimitReached}
                title={studentLimitReached ? `El plan Free permite ${maxStudents} alumnos` : undefined}
              >
                + Nuevo Alumno
              </button>
            </div>
          )}
        </div>
        {studentLimitReached && !showForm && (
          <p className="plan-hint">El plan Free permite hasta {maxStudents} alumnos.</p>
        )}
        {!showForm && (
          <div className="students-filter-bar">
            <label className="students-filter-label" htmlFor="students-search">
              Buscar alumno
            </label>
            <input
              id="students-search"
              ref={searchInputRef}
              type="text"
              placeholder="Nombre, apellido o documento"
              value={searchInput}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearch(searchInput);
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 0);
                  return false;
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }}
              className="students-filter-input"
              autoComplete="off"
              autoFocus={false}
            />
            <div className="students-advanced-filters">
              <input type="number" min="0" max="120" placeholder="Edad mín." value={ageMin}
                onChange={(e) => { setAgeMin(e.target.value); setPage(1); }} />
              <input type="number" min="0" max="120" placeholder="Edad máx." value={ageMax}
                onChange={(e) => { setAgeMax(e.target.value); setPage(1); }} />
              <input type="number" min="1900" max="2200" placeholder="Año nacimiento" value={birthYear}
                onChange={(e) => { setBirthYear(e.target.value); setPage(1); }} />
              <NationalitySelect
                value={nationality}
                onChange={(code) => { setNationality(code); setPage(1); }}
                allowEmpty
                emptyLabel="Todas las nacionalidades"
              />
              <select value={disciplineId} onChange={(e) => { setDisciplineId(e.target.value); setPage(1); }}>
                <option value="">Todas las disciplinas</option>
                {studentDisciplines.map((discipline) => (
                  <option key={discipline.id} value={discipline.id}>{discipline.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {showForm ? (
        <div className="card form-card">
          <div className="form-header">
            <h3>{editingStudent ? '✏️ Editar Alumno' : '➕ Nuevo Alumno'}</h3>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingStudent(null);
              }}
            >
              ← Volver a la lista
            </button>
          </div>
          <StudentForm
            initialData={editingStudent}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingStudent(null);
            }}
            isLoading={createStudentMutation.isLoading || updateStudentMutation.isLoading}
          />
        </div>
      ) : (
        <div className="card">
          {isFetching && studentsData && (
            <div className="students-table-loading">Actualizando lista...</div>
          )}
          <div className="students-bulk-bar">
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => runOnSelected(handleEditClick, { singleOnly: true })}
                disabled={selectedStudents.length !== 1}
              >
                <span className="students-bulk-icon">✏️</span>
                Editar
              </button>
              <button
                type="button"
                className="students-bulk-btn is-danger"
                onClick={() => {
                  if (selectedStudents.length === 1) {
                    handleDeleteStudent(selectedStudents[0]);
                    return;
                  }
                  setConfirmDialog({
                    isOpen: true,
                    title: `¿Eliminar ${selectedStudents.length} alumnos?`,
                    message: 'Dejarán de aparecer en la lista, pero su información se conservará.',
                    onConfirm: () => selectedStudents.forEach((student) => deleteStudentMutation.mutate(student.id)),
                    type: 'danger',
                    confirmText: 'Aceptar',
                    cancelText: 'Cancelar'
                  });
                }}
                disabled={!selectedStudents.length || deleteStudentMutation.isLoading}
              >
                <span className="students-bulk-icon">🗑️</span>
                Eliminar
              </button>
              <button
                type="button"
                className="students-bulk-btn is-warning"
                onClick={() => {
                  if (selectedStudents.length === 1) {
                    handleToggleStatus(selectedStudents[0]);
                    return;
                  }
                  const allActive = selectedStudents.every((student) => student.is_active);
                  const count = selectedStudents.length;
                  showConfirm(
                    `¿${allActive ? 'Desactivar' : 'Activar'} ${count} alumnos?`,
                    allActive
                      ? 'Dejarán de aparecer en la lista. Su información se conserva.'
                      : 'Volverán a aparecer en la lista de alumnos.',
                    () => {
                      selectedStudents.forEach((student) => {
                        toggleStudentStatusMutation.mutate({
                          id: student.id,
                          is_active: !student.is_active
                        });
                      });
                    },
                    allActive ? 'warning' : 'success'
                  );
                }}
                disabled={!selectedStudents.length || toggleStudentStatusMutation.isLoading}
              >
                <span className="students-bulk-icon">⏸️</span>
                {selectedStudents.length === 1 && !selectedStudents[0].is_active ? 'Activar' : 'Desactivar'}
              </button>
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => runOnSelected(handleUpdateEmailClick, { singleOnly: true })}
                disabled={selectedStudents.length !== 1}
              >
                <span className="students-bulk-icon">✉️</span>
                Actualizar Contacto
              </button>
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => runOnSelected(handleGenerateCertificate)}
                disabled={!selectedStudents.length}
              >
                <span className="students-bulk-icon">📄</span>
                Certificado de Asistencia
              </button>
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => runOnSelected((student) => student.qr_code && handleDownloadQR(student))}
                disabled={!selectedStudents.some((student) => student.qr_code)}
              >
                <span className="students-bulk-icon">📱</span>
                Descargar QR
              </button>
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => sendQrToSelected(true)}
                disabled={!selectedStudents.some((student) => student.qr_code && student.parent_email) || sendQREmailMutation.isLoading}
              >
                <span className="students-bulk-icon">✉️</span>
                Enviar QR ({selectedStudents.length === 1
                  ? getParentRelationshipLabel(selectedStudents[0].parent_relationship)
                  : 'Padre/Tutor'})
              </button>
              <button
                type="button"
                className="students-bulk-btn"
                onClick={() => sendQrToSelected(false)}
                disabled={!selectedStudents.some((student) => student.qr_code) || sendQREmailMutation.isLoading}
              >
                <span className="students-bulk-icon">✉️</span>
                Enviar QR (Estudiante)
              </button>
            </div>
          <div
            className="table-container students-table-container"
          >
            <table className="table students-table">
              <thead>
              <tr>
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={students?.length > 0 && students.every((student) => selectedIds.includes(student.id))}
                    onChange={toggleAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
                <th className="col-name">Nombre</th>
                <th>Edad</th>
                <th>Nacionalidad</th>
                <th>Disciplinas</th>
                <th>Documento</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Padre/Tutor</th>
                <th>Estado</th>
              </tr>
              </thead>
              <tbody>
              {students && students.length > 0 ? (
                students.map((student) => (
                  <tr
                    key={student.id}
                    className={`${!student.is_active ? 'inactive-row' : ''} ${selectedIds.includes(student.id) ? 'is-selected' : ''}`}
                  >
                    <td className="col-check">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(student.id)}
                        onChange={() => toggleStudentSelection(student.id)}
                        aria-label={`Seleccionar ${student.first_name} ${student.last_name}`}
                      />
                    </td>
                    <td className="col-name">
                      <button
                        onClick={() => handleEditClick(student)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: '#000',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          font: 'inherit',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        {student.first_name} {student.last_name}
                      </button>
                    </td>
                    <td>{calculateAge(student.birth_date)} años</td>
                    <td><NationalityDisplay code={student.nationality_code} /></td>
                    <td>{student.disciplines?.join(', ') || '—'}</td>
                    <td>
                      {student.document_type && student.document_number
                        ? `${getDocumentTypeLabel(student.document_type)}: ${student.document_number}`
                        : '-'}
                    </td>
                    <td>
                      {(() => {
                        const hasStudentEmail = student.email && student.email.trim() !== '';
                        const hasParentEmail = student.parent_email && student.parent_email.trim() !== '';
                        
                        if (hasStudentEmail && hasParentEmail) {
                          // Mostrar ambos emails
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div style={{ marginBottom: '4px' }}>
                                <strong>Estudiante:</strong> {student.email}
                              </div>
                              <div style={{ color: '#666' }}>
                                <strong>{getParentRelationshipLabel(student.parent_relationship)}:</strong> {student.parent_email}
                              </div>
                            </div>
                          );
                        } else if (hasStudentEmail) {
                          // Solo email del estudiante
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div>
                                <strong>Estudiante:</strong> {student.email}
                              </div>
                            </div>
                          );
                        } else if (hasParentEmail) {
                          // Solo email del padre (mostrar como principal)
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div>
                                <strong>{getParentRelationshipLabel(student.parent_relationship)}:</strong> {student.parent_email}
                              </div>
                            </div>
                          );
                        } else {
                          // No hay emails
                          return <span style={{ color: '#999' }}>-</span>;
                        }
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const hasStudentPhone = student.phone && student.phone.trim() !== '';
                        const hasParentPhone = student.parent_phone && student.parent_phone.trim() !== '';
                        
                        if (hasStudentPhone && hasParentPhone) {
                          // Mostrar ambos teléfonos
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div style={{ marginBottom: '4px' }}>
                                <strong>Estudiante:</strong> {student.phone}
                              </div>
                              <div style={{ color: '#666' }}>
                                <strong>{getParentRelationshipLabel(student.parent_relationship)}:</strong> {student.parent_phone}
                              </div>
                            </div>
                          );
                        } else if (hasStudentPhone) {
                          // Solo teléfono del estudiante
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div>
                                <strong>Estudiante:</strong> {student.phone}
                              </div>
                            </div>
                          );
                        } else if (hasParentPhone) {
                          // Solo teléfono del padre (mostrar como principal)
                          return (
                            <div style={{ fontSize: '12px' }}>
                              <div>
                                <strong>{getParentRelationshipLabel(student.parent_relationship)}:</strong> {student.parent_phone}
                              </div>
                            </div>
                          );
                        } else {
                          // No hay teléfonos
                          return <span style={{ color: '#999' }}>-</span>;
                        }
                      })()}
                    </td>
                    <td>
                      {student.parent_name ? (
                        <span>
                          {student.parent_relationship && (
                            <span style={{ color: '#666', fontSize: '11px' }}>
                              {getParentRelationshipLabel(student.parent_relationship)}:{' '}
                            </span>
                          )}
                          {student.parent_name}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`badge ${student.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {student.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : search ? (
                <tr>
                  <td colSpan="10" className="students-empty-row">
                    No se encontraron alumnos con “{search}”
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="10" style={{ padding: 0, border: 'none' }}>
                    <EmptyState
                      icon="👥"
                      title="Aún no hay alumnos registrados"
                      message="Los estudiantes son la base del sistema. Crea tu primer estudiante para comenzar."
                      primaryAction={handleCreateClick}
                      primaryActionLabel="+ Crear Primer Alumno"
                      hint="Una vez que tengas estudiantes, podrás crear matrículas y gestionar sus actividades."
                      className="compact"
                    />
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          {totalStudents > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Mostrando {pageStart} - {pageEnd} de {totalStudents} alumnos
            </div>
            <div className="pagination-controls">
              <button
                type="button"
                className="pagination-btn"
                onClick={() => setPage(1)}
                disabled={page === 1 || isFetching}
              >
                « Primera
              </button>
              <button
                type="button"
                className="pagination-btn"
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                disabled={page === 1 || isFetching}
              >
                ‹ Anterior
              </button>
              <span className="pagination-current">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                className="pagination-btn"
                onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                disabled={page >= totalPages || isFetching}
              >
                Siguiente ›
              </button>
              <button
                type="button"
                className="pagination-btn"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || isFetching}
              >
                Última »
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStudent(null);
        }}
        title={editingStudent ? 'Editar Alumno' : 'Nuevo Alumno'}
        closeOnOverlayClick={false}
      >
        <StudentForm
          initialData={editingStudent}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingStudent(null);
          }}
          isLoading={createStudentMutation.isLoading || updateStudentMutation.isLoading}
        />
      </Modal>

      {/* Modal para carga masiva */}
      <Modal
        isOpen={bulkUploadModal.isOpen}
        onClose={handleCloseBulkUploadModal}
        title="Carga Masiva de Estudiantes"
        closeOnOverlayClick={false}
      >
        <div style={{ padding: '20px' }}>
          {!bulkUploadModal.result ? (
            <>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  <strong>📋 Instrucciones:</strong>
                </p>
                <ol style={{ marginTop: '10px', marginBottom: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                  <li>Descarga la plantilla Excel haciendo clic en "Descargar Plantilla"</li>
                  <li>Completa los datos de los estudiantes en el archivo</li>
                  <li>Guarda el archivo y súbelo aquí</li>
                  <li><strong>Campos obligatorios:</strong> Nombre, Apellidos, Fecha de Nacimiento y <strong>Número de Documento (DNI)</strong></li>
                  <li><strong>Para menores de edad:</strong> Es obligatorio incluir el Nombre del Padre/Tutor</li>
                </ol>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  className="btn btn-success"
                  onClick={handleDownloadTemplate}
                  style={{ width: '100%', padding: '15px' }}
                >
                  📥 Descargar Plantilla Excel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={bulkUploadModal.uploading}
                  style={{ width: '100%', padding: '15px' }}
                >
                  {bulkUploadModal.uploading ? '⏳ Procesando...' : '📤 Seleccionar Archivo Excel'}
                </button>
              </div>
            </>
          ) : (
            <div>
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: bulkUploadModal.result.created > 0 ? '#d4edda' : '#f8d7da', borderRadius: '8px', border: `1px solid ${bulkUploadModal.result.created > 0 ? '#c3e6cb' : '#f5c6cb'}` }}>
                <h4 style={{ margin: '0 0 10px 0', color: bulkUploadModal.result.created > 0 ? '#155724' : '#721c24' }}>
                  {bulkUploadModal.result.created > 0 ? '✅ Carga Completada' : '⚠️ Carga con Errores'}
                </h4>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  <strong>Total de filas:</strong> {bulkUploadModal.result.total_rows}<br />
                  <strong>Estudiantes creados:</strong> {bulkUploadModal.result.created}<br />
                  <strong>Errores:</strong> {bulkUploadModal.result.errors}
                </p>
              </div>

              {bulkUploadModal.result.created_students?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ color: '#28a745', marginBottom: '10px' }}>✅ Estudiantes Creados Exitosamente ({bulkUploadModal.result.created}):</h5>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #28a745', borderRadius: '4px', padding: '10px', backgroundColor: '#f8fff9' }}>
                    {bulkUploadModal.result.created_students.map((student, idx) => (
                      <div key={idx} style={{ padding: '8px', marginBottom: '5px', borderBottom: idx < bulkUploadModal.result.created_students.length - 1 ? '1px solid #d4edda' : 'none', fontSize: '14px' }}>
                        <strong style={{ color: '#28a745' }}>✓</strong> <strong>Fila {student.row}:</strong> {student.student_name || student.name}
                        {student.document_number && student.document_number !== 'Sin documento' && (
                          <span style={{ marginLeft: '10px', color: '#666', fontSize: '13px' }}>({student.document_number})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bulkUploadModal.result.error_details?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h5 style={{ color: '#dc3545', marginBottom: '10px' }}>⚠️ Estudiantes NO Cargados ({bulkUploadModal.result.errors}):</h5>
                  <div style={{ maxHeight: '250px', overflowY: 'auto', border: '2px solid #dc3545', borderRadius: '4px', padding: '10px', backgroundColor: '#fff5f5' }}>
                    {bulkUploadModal.result.error_details.map((error, idx) => (
                      <div key={idx} style={{ padding: '10px', marginBottom: '8px', borderBottom: idx < bulkUploadModal.result.error_details.length - 1 ? '1px solid #ffdddd' : 'none', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ffdddd' }}>
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#dc3545' }}>❌ Fila {error.row}:</strong> 
                          <strong style={{ marginLeft: '8px', color: '#333' }}>{error.student_name || 'Sin nombre'}</strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', marginLeft: '20px' }}>
                          {error.error}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                    <strong style={{ color: '#856404' }}>💡 Sugerencia:</strong>
                    <span style={{ marginLeft: '5px', fontSize: '13px', color: '#856404' }}>
                      Corrige los errores en el archivo Excel y vuelve a cargar solo las filas que fallaron.
                    </span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setBulkUploadModal({ isOpen: false, uploading: false, result: null })}
                >
                  Cerrar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setBulkUploadModal({ isOpen: true, uploading: false, result: null });
                  }}
                >
                  Cargar Otro Archivo
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal para actualizar emails */}
      <Modal
        isOpen={emailModal.isOpen}
        onClose={() => setEmailModal({ isOpen: false, student: null })}
        title={`Actualizar Contacto - ${emailModal.student?.first_name} ${emailModal.student?.last_name}`}
        closeOnOverlayClick={false}
      >
        {emailModal.student && (
          <form onSubmit={handleUpdateEmailSubmit} className="update-email-form">
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                <strong>💡 Nota:</strong> Puedes actualizar los emails y teléfonos del estudiante y padre/tutor. 
                Si actualizas un email y marcas la opción, se reenviarán las notificaciones (QR/PDF) al nuevo email.
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="student_email">Email del Estudiante</label>
              <input
                type="email"
                id="student_email"
                name="student_email"
                defaultValue={emailModal.student.email || ''}
                placeholder="email@ejemplo.com"
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                {typeof calculateAge(emailModal.student.birth_date) === 'number' && calculateAge(emailModal.student.birth_date) < 18 && !(emailModal.student.parent_email || '').trim()
                  ? 'Si el alumno es menor y el padre no tiene correo, este mismo email también se guarda como el del padre/tutor.'
                  : 'Dejar vacío si no deseas actualizar este campo'}
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="parent_email">Email del Padre/Tutor</label>
              <input
                type="email"
                id="parent_email"
                name="parent_email"
                defaultValue={emailModal.student.parent_email || ''}
                placeholder="email@ejemplo.com"
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Dejar vacío si no deseas actualizar este campo
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="student_phone">Teléfono del Estudiante</label>
              <input
                type="tel"
                id="student_phone"
                name="student_phone"
                defaultValue={emailModal.student.phone || ''}
                placeholder="+51 900 000 000"
                onInput={handlePhoneInput}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Dejar vacío si no deseas actualizar este campo
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="parent_phone">Teléfono del Padre/Tutor</label>
              <input
                type="tel"
                id="parent_phone"
                name="parent_phone"
                defaultValue={emailModal.student.parent_phone || ''}
                placeholder="+51 900 000 000"
                onInput={handlePhoneInput}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Dejar vacío si no deseas actualizar este campo
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="resend_notifications"
                  defaultChecked={true}
                  style={{ marginRight: '8px', width: '18px', height: '18px' }}
                />
                <span>Reenviar notificaciones (QR/PDF) si hay matrícula activa</span>
              </label>
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                Si está marcado, se reenviará automáticamente el QR y PDF de la matrícula activa al email actualizado
              </small>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setEmailModal({ isOpen: false, student: null })}
                disabled={updateEmailMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateEmailMutation.isLoading}
                style={{ backgroundColor: '#e74c3c' }}
              >
                {updateEmailMutation.isLoading ? 'Actualizando...' : 'Actualizar Contacto'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
    </>
  );
};

export default Students;
